import { get, isEmpty, isEqual, map, property } from 'lodash/fp';
import { createSelectorCreator, lruMemoize } from 'reselect';
import { DateTime, Settings } from 'luxon';
import i18next from 'i18next';
import { errorToI18n } from 'app/lib/error';
import type { ExtractedError } from 'app/lib/error/types';
import {
    UNKNOWN_COLUMN_NAMES_TYPE,
    UNKNOWN_COLUMN_NAMES_WITH_SOURCE_TYPE,
    DUPLICATE_COLUMN_NAMES_TYPE,
    MISSING_SOURCE_COLUMN_TYPE
} from 'app/store/__generated__/types';
import {
    UNKNOWN_COLUMN_NAMES,
    UNKNOWN_COLUMN_NAMES_WITH_SOURCE,
    DUPLICATE_COLUMN_NAME,
    MISSING_SOURCE_COLUMN,
    TYPES_NOT_COERCIBLE,
    COLUMN_REMOVED_UPSTREAM,
    TYPE_COERCION,
    COLUMN_ADDED_UPSTREAM,
    UNEXPECTED_COLUMN,
    EXPECTED_COLUMN_MISSING
} from 'app/store/__generated__/errors';
import { DeploymentOutOfSyncProperty } from 'app/lib/types/server';
import { ERROR_CATEGORY, ErrorLevel } from 'app/lib/constants';
import { COMMAND_CHAIN_COMPLETED } from './commandChain/types';

Settings.defaultLocale = DateTime.now().resolvedLocaleOptions().locale;

export const createDeepEqualitySelector = createSelectorCreator(lruMemoize, isEqual);

// would be cleaner with propertyOf, but this has a bug in the type definitions:
// https://github.com/DefinitelyTyped/DefinitelyTyped/discussions/62373
export const resolveIds = (byId: Record<string, unknown>, ids: string[]) =>
    map((id) => property(id, byId), ids);

export const hasDifferentSchema = (entity: Record<string, Record<string, string>>) => {
    const columnsDiff = get('columnsDiff', entity);

    if (!columnsDiff) {
        return false;
    }

    return (
        !isEmpty(columnsDiff.added) ||
        !isEmpty(columnsDiff.changed) ||
        !isEmpty(columnsDiff.removed)
    );
};

export const hasStaleData = (
    deployedView: { introspectedAt: string },
    parentView: { modifiedAt: string }
) => {
    const deployedDate = DateTime.fromISO(deployedView.introspectedAt);
    const parentModifiedDate = DateTime.fromISO(parentView.modifiedAt);

    return parentModifiedDate > deployedDate;
};

export class ApiActionType<TScope extends string, TAction extends string> extends String {
    scope: TScope;
    action: TAction;
    type: `${TScope}/${TAction}`;

    constructor(scope: TScope, action: TAction) {
        const type: `${TScope}/${TAction}` = `${scope}/${action}`;
        super(type);
        this.scope = scope;
        this.action = action;
        this.type = type;
    }

    get STARTED(): `${TScope}/${TAction}/started` {
        return `${this.type}/started`;
    }

    get REJECTED(): `${TScope}/${TAction}/rejected` {
        return `${this.type}/rejected`;
    }

    get CANCELED(): `${TScope}/${TAction}/canceled` {
        return `${this.type}/canceled`;
    }

    get PROGRESS(): `${TScope}/${TAction}/progress` {
        return `${this.type}/progress`;
    }

    get SUCCEEDED(): `${TScope}/${TAction}/succeeded` {
        return `${this.type}/succeeded`;
    }

    get FAILED(): `${TScope}/${TAction}/failed` {
        return `${this.type}/failed`;
    }

    get ABORTED(): `${TScope}/${TAction}/aborted` {
        return `${this.type}/aborted`;
    }

    get TIMEDOUT(): `${TScope}/${TAction}/timedout` {
        return `${this.type}/timedout`;
    }

    get COMMAND_CHAIN_COMPLETED(): `${TScope}/${TAction}/${typeof COMMAND_CHAIN_COMPLETED}` {
        return `${this.type}/${COMMAND_CHAIN_COMPLETED}`;
    }

    get ENDED(): `${TScope}/${TAction}/ended` {
        return `${this.type}/ended`;
    }
}

export const createApiActionType =
    <TScope extends string>(scope: TScope) =>
    <TAction extends string>(action: TAction) =>
        new ApiActionType(scope, action);

export type ActionType<TScope extends string, TAction extends string> = `${TScope}/${TAction}`;
export const createActionType =
    <TScope extends string>(scope: TScope) =>
    <TAction extends string>(action: TAction): ActionType<TScope, TAction> =>
        `${scope}/${action}`;

export const extractViewId = (id: string) => {
    const [viewId] = id.split('_op');
    return viewId;
};

export const resolveErrorType = (type: string) => {
    switch (type) {
        case UNKNOWN_COLUMN_NAMES_TYPE:
            return UNKNOWN_COLUMN_NAMES;
        case UNKNOWN_COLUMN_NAMES_WITH_SOURCE_TYPE:
            return UNKNOWN_COLUMN_NAMES_WITH_SOURCE;
        case DUPLICATE_COLUMN_NAMES_TYPE:
            return DUPLICATE_COLUMN_NAME;
        case MISSING_SOURCE_COLUMN_TYPE:
            return MISSING_SOURCE_COLUMN;
        case TYPES_NOT_COERCIBLE:
            return TYPES_NOT_COERCIBLE;
        case EXPECTED_COLUMN_MISSING:
            return EXPECTED_COLUMN_MISSING;
        case COLUMN_REMOVED_UPSTREAM:
            return COLUMN_REMOVED_UPSTREAM;
        case UNEXPECTED_COLUMN:
            return UNEXPECTED_COLUMN;
        default:
            return 'GENERIC_ERROR';
    }
};

export const resolveErrorCategory = (type: string) => {
    switch (type) {
        case UNKNOWN_COLUMN_NAMES_TYPE:
        case UNKNOWN_COLUMN_NAMES_WITH_SOURCE_TYPE:
        case DUPLICATE_COLUMN_NAMES_TYPE:
        case MISSING_SOURCE_COLUMN_TYPE:
        case DeploymentOutOfSyncProperty.COLUMNS_ADDED_UPSTREAM:
        case DeploymentOutOfSyncProperty.COLUMNS_REMOVED_UPSTREAM:
        case DeploymentOutOfSyncProperty.COLUMNS_TYPE_CHANGED_UPSTREAM:
        case TYPES_NOT_COERCIBLE:
        case COLUMN_REMOVED_UPSTREAM:
        case TYPE_COERCION:
        case COLUMN_ADDED_UPSTREAM:
        case UNEXPECTED_COLUMN:
        case EXPECTED_COLUMN_MISSING:
            return ERROR_CATEGORY.schema;
        default:
            return ERROR_CATEGORY.compilation;
    }
};

const mapToI18n = errorToI18n();
export function getTranslatedError(error: ExtractedError) {
    if (isEmpty(error)) {
        return null;
    }
    const i18nError = mapToI18n(error);
    const translatedMsg = isEmpty(i18nError) ? null : i18next.t(i18nError.key, i18nError.data);
    return {
        ...error,
        values: { message: translatedMsg },
        type: error.level === ErrorLevel.ERROR ? 'GENERIC_ERROR' : 'GENERIC_WARNING'
    };
}
